import { ColDef, PostSortRowsParams, RowClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { aggridHeaderClass, MaximizeIcon, RestoreIcon, SmallTitleSpan, titleMRClass } from "src/components/common/common";
import useUiuxStore from "src/store/useUiuxStore";
import { CreateCellClickHandler, createCellCssClassRules, CreateCellDoubleClickHandler, CustomCellClickAction, CustomCellDoubleClickAction, GridCellConfig, InsertRowHandler, postSortHandler, RemoveRowHandler, useCellChangeHandler, useRowSelectionHandler } from "src/utils/grid/GridHandlerContent";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags, IndexParameterCell } from "src/utils/grid/gridStyleUtils";
import { usePRD3012_R01Store } from "./Store_PRD3012_R01";

import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { defaultPRD3012_R01DetailDto, PRD3012_R01DetailDto, PRD3012_R01InspectDto } from "./Dto_PRD3012_R01";

import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { useDetailTableHandlers as useCommonDetailTableHandlers } from "src/hooks/useDetailTableHandlers";

// 범용 훅 임포트
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";

const COLUMN_STATE_KEY = 'PRD3012_R01-detail-grid-column-state';

const GRID_CELL_CONFIG: GridCellConfig = {
	disabledCells: ['warehouseNm', 'childItemcd', 'itemNm', 'itemSpec', 'lotNo', 'lotQty', 'bomUsingQty'],
	insertedDisabledCells: [''],
	commonDisabledCells: [''],
	
	pointerCells: ['transportQty'],
	insertedPointerCells: [''],
	commonPointerCells: [''],
	
	alignLeftCells: ['']
};

interface Props {
	gridRef: React.RefObject<AgGridReact<PRD3012_R01DetailDto> | null>;
	defaultPRD3012_R01DetailRow: PRD3012_R01DetailDto;
	isMaximized?: boolean;
	onToggleMaximize?: () => void;
}

export const DetailTable_PRD3012_R01 = (props: Props) => {

	const { gridRef, defaultPRD3012_R01DetailRow, isMaximized = false, onToggleMaximize } = props;

	const { 
			PRD3012_R01DetailList, setPRD3012_R01DetailList, 
			editPRD3012_R01,
			selectedPRD3012_R01DetailList, setSelectedPRD3012_R01DetailList, removeSelectedPRD3012_R01DetailList, addSelectedPRD3012_R01DetailList, initSelectedPRD3012_R01DetailList,
			deletedPRD3012_R01DetailList, setDeletedPRD3012_R01DetailList, addDeletedPRD3012_R01DetailList, removeDeletedPRD3012_R01DetailList,
			insertPRD3012_R01DetailList, updatePRD3012_R01DetailList, removePRD3012_R01DetailList,
			detailColumnState, setDetailColumnState,
			originalPRD3012_R01DetailList, setOriginalPRD3012_R01DetailList,
		} = usePRD3012_R01Store();
	const { setCodeSearchModalProps } = useUiuxStore();
	const { itemStatusList } = useCommonCodeStore();
	const [clickable, setClickable] = useState(true);

	//const gridRef = useRef<AgGridReact>(null);
	const gridWrapperRef = useRef<HTMLDivElement>(null); // 1. div를 위한 ref 생성

	const {
		getRowId,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
		renderIndexCell,
	} = useGridSettingContent({
		gridRef,
		key: 'valueIndex',
		gridData: PRD3012_R01DetailList,
		defaultGridData: defaultPRD3012_R01DetailRow,
		selectedRow: selectedPRD3012_R01DetailList,
		originalGridData: originalPRD3012_R01DetailList,
		updateRow: (index: number, data: any) => updatePRD3012_R01DetailList(index, data),
	});

	const headerNames: Record<string, string> = {
		rowIndex: "",
		warehouseNm: '출고창고',
		itemCd: '자재코드',
		itemNm: '품명',
		itemSpec: '규격',
		lotNo: 'Lot No',
		lotQty: '재고량',
		bomUsingQty: '소요량',
		transportQty: '출고량',
	}

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = createCellCssClassRules(GRID_CELL_CONFIG);

	const gridDefaultColDef = useMemo(() => {
        const baseRules = createCellClassRules<PRD3012_R01DetailDto>({
            idKey: 'valueIndex',
            selectedList: selectedPRD3012_R01DetailList,
            deletedList: deletedPRD3012_R01DetailList,
            disabledInsertedColumns,	disabledColumns,
            pointerInsertedColumns,		pointerColumns,
            alignLeftColumns,
        });

        return { ...defaultColDef, cellClassRules: { ...baseRules, }}; 
	}, [
        selectedPRD3012_R01DetailList, deletedPRD3012_R01DetailList,
        disabledInsertedColumns, disabledColumns,         
        pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
    ]);
	const baseColumnDefs: ColDef<PRD3012_R01DetailDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(
		),
		{
            headerName: '출고창고', colId: 'warehouseNm', field: 'warehouseNm', width: 80,
			editable: false,
		},
        {
            headerName: '자재코드', colId: 'childItemcd', field: 'childItemcd', width: 120,
			editable: false,
		},
		{
            headerName: '품명', colId: 'itemNm', field: 'itemNm', width: 200,
			editable: false,
		},
		{
            headerName: '규격', colId: 'itemSpec', field: 'itemSpec', width: 200,
			editable: false,
		},
		{
            headerName: 'Lot No', colId: 'lotNo', field: 'lotNo', width: 140,
			editable: false,
		},
		{
            headerName: '재고량', colId: 'lotQty', field: 'lotQty', width: 75,
			editable: false,
			valueFormatter: (params: any) =>
				params.value != null ? params.value.toLocaleString() : '',
		},
		// {
        //		headerName: '개당소요량', colId: 'childItemUsage', field: 'childItemUsage', width: 90,
		//		editable: false,
		// },
		// {
        //		headerName: '손실률', colId: 'lossPercent', field: 'lossPercent', width: 90,
		//		editable: false,
		// },
		{
            headerName: '소요량', colId: 'bomUsingQty', field: 'bomUsingQty', width: 75,
			editable: false,
			valueFormatter: (params: any) =>
				params.value != null ? params.value.toLocaleString() : '',
		},
		// {
        //		headerName: '출고완료량', colId: 'transportedQty', field: 'transportedQty', width: 65,
		//		editable: false,
		// },
		{
            headerName: '출고량', colId: 'transportQty', field: 'transportQty', width: 75,
			editable: true,
			pasteable: true,
			valueFormatter: (params: any) =>
				params.value != null ? params.value.toLocaleString() : '',
		},
		// ...IndexParameterCell(),
	], [deletedPRD3012_R01DetailList, PRD3012_R01DetailList]);

	const columnDefs: ColDef<PRD3012_R01DetailDto>[] = useMemo(() => {
		if (detailColumnState === null) {
			return baseColumnDefs;
		}
		
		return GridCustomColumnDefs<PRD3012_R01DetailDto>({
			baseColumnDefs,
			columnState: detailColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass
		});
	}, [baseColumnDefs, detailColumnState, headerNames]);

	
	const handleCellChange = useCellChangeHandler({
		dataList: PRD3012_R01DetailList,
		originalDataList: originalPRD3012_R01DetailList,
		updateRow: updatePRD3012_R01DetailList,
		customBeforeUpdate: (updatedData, field, value) => {
			return updatedData;
		},
		customAfterUpdate: (updatedData, field, value) => {
			if(field === 'transportQty') {
				if(updatedData.transportQty === 0) {
					updatedData.isModified = false;
				}
			}
			return updatedData;
		},
	});
	

	const handleRowClick = useRowSelectionHandler({
		dataList: PRD3012_R01DetailList,
		idKey: 'valueIndex',
		selectedList: selectedPRD3012_R01DetailList,
		deletedList: deletedPRD3012_R01DetailList,
		addSelected: addSelectedPRD3012_R01DetailList,
		removeSelected: removeSelectedPRD3012_R01DetailList,
		clickable, setClickable
	});

	const commonInputColumns = ['transportQty', ];

	// 범용 이벤트 훅을 위한 설정
	const defaultEditableColumns = ['transportQty', ];


	// 범용 이벤트 훅 사용 (코드 검색 없이) - 방향키 이동 기능 유지
	/**
	 * 기본 상세 테이블 이벤트 핸들러 훅
	 * 
	 * 주요 기능:
	 * - 키다운 네비게이션 (방향키 이동)
	 * - 셀 클릭/키다운 이벤트 처리
	 * 
	 * @param detailList - 상세 데이터 목록
	 * @param editableColumns - 편집 가능한 컬럼 목록
	 */
	const cellEventHandlers = useDetailTableEvents({
		detailList: PRD3012_R01DetailList as any,
		editableColumns: defaultEditableColumns,
		keyDownConfig: {
			enableArrowNavigation: true,
		}
	});
	
	const cellClickActions: CustomCellClickAction<PRD3012_R01DetailDto>[] = useMemo(() => [
		{
			validate: (field, data) => {
				return commonInputColumns.includes(field);
			},
			//aggrid 기본 편집 기능
			action: (e) => { e.api.startEditingCell({ rowIndex: e.rowIndex!, colKey: e.colDef.field! }); }
		},
	], []);
	const handleCellClick = CreateCellClickHandler(PRD3012_R01DetailList, 'valueIndex', cellClickActions);

	//더블클릭 액션 없음
	const cellDoubleClickActions: CustomCellDoubleClickAction<PRD3012_R01DetailDto>[] = useMemo(() => [
	], [editPRD3012_R01?.itemCd]);
	const handleCellDoubleClick = CreateCellDoubleClickHandler(PRD3012_R01DetailList, 'valueIndex', cellDoubleClickActions);


	const handlePostSort = useCallback((params: PostSortRowsParams<PRD3012_R01DetailDto>) => {
		postSortHandler(params, PRD3012_R01DetailList, originalPRD3012_R01DetailList, setPRD3012_R01DetailList);
	}, [PRD3012_R01DetailList, originalPRD3012_R01DetailList, ]);

	const pinnedBottomRowData = useMemo(() => {
		return [{
			warehouseNm: PRD3012_R01DetailList.length.toLocaleString() + "건",
			// lotQty: PRD3012_R01DetailList.reduce((sum, item) => sum + (deletedPRD3012_R01DetailList.includes(item.valueIndex.toString()) ? 0 : Number(item.lotQty) || 0), 0),
		}];	  
	}, [PRD3012_R01DetailList, deletedPRD3012_R01DetailList,]);

	return (
		<>
			<div className="flex flex-1 h-full min-h-full">
				<GridContextWrapper<PRD3012_R01DetailDto>
					gridWrapperRef={gridWrapperRef}
					gridRef={gridRef}
					paddedGridData={paddedGridData}
					setPaddedGridData={undefined}
					// setPaddedGridData={setPRD3012_R01DetailList}
					initSelected={initSelectedPRD3012_R01DetailList}
					defaultRowData={defaultPRD3012_R01DetailRow}
					baseColumnDefs={columnDefs}	
					setColumnState={setDetailColumnState}
					localStorageKey={COLUMN_STATE_KEY}
					menuItems={undefined}
				>
					<SmallTitleSpan>
						<span className="inline-flex items-center gap-[4px]">
							자재출고
							<button
								className="relative bg-blue-dark text-white py-[1px] rounded-[4px] px-[8px]"
								onClick={onToggleMaximize}
								title={isMaximized ? '최소화' : '최대화'}
							>
								{isMaximized ? <RestoreIcon /> : <MaximizeIcon />}
							</button>
						</span>
					</SmallTitleSpan>
					<Grid_Excel<PRD3012_R01DetailDto>
						ref={gridRef}
						rowData={paddedGridData}	getRowId={getRowId}
						columnDefs={columnDefs}		
						defaultColDef={gridDefaultColDef}

						rowClassRules={createRowClassRules<PRD3012_R01DetailDto>({
							idKey: 'valueIndex',
							selectedList: selectedPRD3012_R01DetailList,
							deletedList: deletedPRD3012_R01DetailList
						})}

						onRowClicked={handleRowClick}
						onCellClicked={handleCellClick}
						onCellDoubleClicked={handleCellDoubleClick}
						onCellKeyDown={cellEventHandlers.handleCellKeyDown}
						onCellValueChanged={handleCellChange}
						pinnedBottomRowData={pinnedBottomRowData}
						

						onPostSortRows={handlePostSort} //onSortChanged 삭제, 커스텀 정렬 기능추가 
						// Grid_Select에 공통 로직 처리를 위한 props 전달
						columnStateKey={COLUMN_STATE_KEY}			//컬럼 상태 키(로컬스토리지)	
						setColumnState={setDetailColumnState}		//컬럼 상태 저장
					/>
				</GridContextWrapper>
			</div>
		</>
	)
}